import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import "./SlideToConfirm.css";

/**
 * SlideToConfirm Component
 * Interactive slide-to-unlock style confirmation dialog
 *
 * @param {boolean} show - Controls visibility of the modal
 * @param {function} onConfirm - Callback when slide is completed
 * @param {function} onCancel - Callback when canceled
 * @param {string} title - Modal title
 * @param {string} message - Warning message to display
 * @param {string} slideText - Text shown on the slider (default: "Zum Löschen schieben")
 * @param {string} variant - Color variant: 'danger', 'warning', 'success' (default: 'danger')
 * @param {boolean} showBulkDeleteOption - Show checkbox for bulk delete (default: false)
 * @param {function} onBulkDeleteToggle - Callback when bulk delete checkbox is toggled
 */
const SlideToConfirm = ({
    show,
    onConfirm,
    onCancel,
    title = "Bestätigung erforderlich",
    message = "Bitte bestätigen Sie diese Aktion.",
    slideText = "Zum Löschen schieben",
    variant = "danger",
    showBulkDeleteOption = false,
    onBulkDeleteToggle = () => {}
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);
    const [particles, setParticles] = useState([]);
    const [bulkDeleteEnabled, setBulkDeleteEnabled] = useState(false);
    const sliderRef = useRef(null);
    const trackRef = useRef(null);
    const startPosRef = useRef(0);
    const lastSparkTimeRef = useRef(0);
    const particleIdRef = useRef(0);
    const rotationAngleRef = useRef(0);

    const SLIDE_THRESHOLD = 0.85; // 85% to complete
    const SPARK_INTERVAL = 20; // Milliseconds between spark emissions (very fast!)

    useEffect(() => {
        if (!show) {
            // Reset state when modal closes
            setPosition(0);
            setIsCompleted(false);
            setIsDragging(false);
            setParticles([]);
            setBulkDeleteEnabled(false);
        }
    }, [show]);

    // Particle animation loop - circular saw physics with large spread
    useEffect(() => {
        if (particles.length === 0) return;

        const animationFrame = requestAnimationFrame(() => {
            setParticles(prevParticles =>
                prevParticles
                    .map(particle => ({
                        ...particle,
                        x: particle.x + particle.vx,
                        y: particle.y + particle.vy,
                        vx: particle.vx * 0.96, // Less air resistance for larger spread
                        vy: particle.vy + 0.6, // Moderate gravity
                        opacity: particle.opacity - 0.012, // Slower fade for visibility
                        life: particle.life - 1,
                        rotation: particle.rotation + 20 // Fast spinning sparks
                    }))
                    .filter(particle => particle.life > 0 && particle.opacity > 0)
            );
        });

        return () => cancelAnimationFrame(animationFrame);
    }, [particles]);

    const createSparks = (x, y) => {
        const now = Date.now();
        if (now - lastSparkTimeRef.current < SPARK_INTERVAL) return;
        lastSparkTimeRef.current = now;

        // Simulate rotating saw blade - increment rotation angle
        rotationAngleRef.current += 25; // Fast rotation

        // Create sparks around the entire circular perimeter (reduced count)
        const newSparks = Array.from({ length: 6 }, (_, i) => {
            // Distribute sparks around a circle (360°)
            // Add rotation offset to simulate spinning blade
            const segmentAngle = (Math.PI * 2 / 6) * i;
            const angle = rotationAngleRef.current * (Math.PI / 180) + segmentAngle;

            // Add small random variation to angle
            const angleVariation = (Math.random() - 0.5) * 0.3;
            const finalAngle = angle + angleVariation;

            // Sparks shoot outward from the button edge
            const buttonRadius = 28; // Half of slider width (56px / 2)
            const startDistance = buttonRadius + Math.random() * 5;

            // Starting position on the circle edge
            const startX = Math.cos(finalAngle) * startDistance;
            const startY = Math.sin(finalAngle) * startDistance;

            // High speed shooting outward
            const speed = 8 + Math.random() * 15;

            // Velocity in the radial direction + some tangential (spinning effect)
            const tangentialSpeed = Math.random() * 3;
            const vx = Math.cos(finalAngle) * speed + Math.cos(finalAngle + Math.PI / 2) * tangentialSpeed;
            const vy = Math.sin(finalAngle) * speed + Math.sin(finalAngle + Math.PI / 2) * tangentialSpeed;

            return {
                id: particleIdRef.current++,
                x: x + startX,
                y: y + startY,
                vx: vx,
                vy: vy + Math.random() * 2, // Slight downward bias
                opacity: 1,
                life: 50 + Math.random() * 40, // Longer life for larger spread
                size: 2 + Math.random() * 5,
                // Slotify red only - danger red (#dc3545)
                color: '#dc3545',
                rotation: Math.random() * 360,
                trail: Math.random() > 0.6
            };
        });

        setParticles(prev => [...prev, ...newSparks]);
    };

    const getMaxPosition = () => {
        if (!trackRef.current || !sliderRef.current) return 0;
        return trackRef.current.offsetWidth - sliderRef.current.offsetWidth;
    };

    const handleStart = (clientX) => {
        setIsDragging(true);
        startPosRef.current = clientX - position;
    };

    const handleMove = (clientX) => {
        if (!isDragging) return;

        const maxPos = getMaxPosition();
        const newPos = clientX - startPosRef.current;

        if (newPos >= 0 && newPos <= maxPos) {
            setPosition(newPos);

            // Create sparks while dragging
            if (sliderRef.current && trackRef.current) {
                const sliderRect = sliderRef.current.getBoundingClientRect();
                const trackRect = trackRef.current.getBoundingClientRect();
                const sparkX = sliderRect.left + sliderRect.width - trackRect.left;
                const sparkY = sliderRect.top + sliderRect.height / 2 - trackRect.top;
                createSparks(sparkX, sparkY);
            }

            // Check if completed
            if (newPos >= maxPos * SLIDE_THRESHOLD && !isCompleted) {
                setIsCompleted(true);
                setIsDragging(false);

                // Haptic feedback (if supported)
                if (navigator.vibrate) {
                    navigator.vibrate(50);
                }

                // Call onConfirm after animation
                setTimeout(() => {
                    onConfirm();
                }, 300);
            }
        }
    };

    const handleEnd = () => {
        if (!isCompleted) {
            // Snap back if not completed
            setPosition(0);
        }
        setIsDragging(false);
    };

    // Mouse events
    const handleMouseDown = (e) => {
        e.preventDefault();
        handleStart(e.clientX);
    };

    const handleMouseMove = (e) => {
        handleMove(e.clientX);
    };

    const handleMouseUp = () => {
        handleEnd();
    };

    // Touch events
    const handleTouchStart = (e) => {
        handleStart(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
        handleMove(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
        handleEnd();
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleTouchEnd);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
            };
        }
    }, [isDragging, position]);

    if (!show) return null;

    const progress = (position / getMaxPosition()) * 100 || 0;
    const variantClass = `slide-confirm-${variant}`;

    // Calculate smooth color transition from red to green based on progress
    const getProgressColor = () => {
        if (isCompleted) {
            return '#28a745'; // Green when completed
        }

        // Interpolate between red and green based on progress
        const progressNormalized = Math.min(progress / 100, 1);

        // Red color: rgb(220, 53, 69) -> #dc3545
        // Green color: rgb(40, 167, 69) -> #28a745
        const r = Math.round(220 - (220 - 40) * progressNormalized);
        const g = Math.round(53 + (167 - 53) * progressNormalized);
        const b = Math.round(69);

        return `rgb(${r}, ${g}, ${b})`;
    };

    // Calculate text color transition from gray to white based on progress
    // Start fading at 35% progress
    const getTextColor = () => {
        if (isCompleted) {
            return '#ffffff';
        }

        const FADE_START = 0.35; // Start fading at 35%
        const progressNormalized = Math.min(progress / 100, 1);

        // No color change before 35%
        if (progressNormalized < FADE_START) {
            return '#6c757d';
        }

        // Scale progress from 35-100% to 0-1
        const fadeProgress = (progressNormalized - FADE_START) / (1 - FADE_START);

        // Start color: #6c757d (gray)
        // End color: #ffffff (white)
        const r = Math.round(108 + (255 - 108) * fadeProgress);
        const g = Math.round(117 + (255 - 117) * fadeProgress);
        const b = Math.round(125 + (255 - 125) * fadeProgress);

        return `rgb(${r}, ${g}, ${b})`;
    };

    return ReactDOM.createPortal(
        <div className="slide-confirm-overlay" onClick={onCancel}>
            <div className="slide-confirm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="slide-confirm-header">
                    <h5 className="slide-confirm-title">{title}</h5>
                    <button
                        type="button"
                        className="slide-confirm-close"
                        onClick={onCancel}
                        aria-label="Close"
                    >
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>

                <div className="slide-confirm-body">
                    <div className={`slide-confirm-message ${variantClass}`}>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {message}
                    </div>

                    {showBulkDeleteOption && (
                        <div className="slide-confirm-bulk-option">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="bulkDeleteCheck"
                                    checked={bulkDeleteEnabled}
                                    onChange={(e) => {
                                        setBulkDeleteEnabled(e.target.checked);
                                        onBulkDeleteToggle(e.target.checked);
                                    }}
                                />
                                <label className="form-check-label" htmlFor="bulkDeleteCheck">
                                    <strong>Ich lösche gerade viele Einträge.</strong> Den Sicherheits-Slider für diese Session nicht mehr anzeigen.
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="slide-confirm-instruction">
                        <i className="bi bi-arrow-right me-2"></i>
                        Ziehen Sie den Button nach rechts, um zu bestätigen
                    </div>

                    <div
                        ref={trackRef}
                        className={`slide-confirm-track ${variantClass} ${isCompleted ? 'completed' : ''}`}
                    >
                        <div
                            className="slide-confirm-progress"
                            style={{
                                width: `${progress}%`,
                                background: getProgressColor(),
                                transition: 'width 0.1s linear, background 0.15s ease-out'
                            }}
                        />

                        <div
                            className="slide-confirm-text"
                            style={{
                                color: getTextColor(),
                                transition: 'color 0.15s ease-out'
                            }}
                        >
                            {isCompleted ? (
                                <>
                                    <i className="bi bi-check-lg me-2"></i>
                                    Bestätigt!
                                </>
                            ) : (
                                slideText
                            )}
                        </div>

                        {/* Spark Particles - Circular saw effect */}
                        {particles.map(particle => {
                            const trackRect = trackRef.current?.getBoundingClientRect();
                            if (!trackRect) return null;

                            // Elongated spark shape for metal particles
                            const sparkWidth = particle.size;
                            const sparkHeight = particle.size * (particle.trail ? 2.5 : 1.5);

                            return (
                                <div
                                    key={particle.id}
                                    className="slide-confirm-particle-global"
                                    style={{
                                        left: `${trackRect.left + particle.x}px`,
                                        top: `${trackRect.top + particle.y}px`,
                                        width: `${sparkWidth}px`,
                                        height: `${sparkHeight}px`,
                                        backgroundColor: particle.color,
                                        opacity: particle.opacity,
                                        transform: `rotate(${particle.rotation}deg)`,
                                        color: particle.color // For box-shadow currentColor
                                    }}
                                />
                            );
                        })}

                        <div
                            ref={sliderRef}
                            className={`slide-confirm-slider ${isDragging ? 'dragging' : ''} ${isCompleted ? 'completed' : ''}`}
                            style={{
                                left: `${position}px`,
                                transition: isDragging ? 'none' : 'left 0.3s ease-out'
                            }}
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleTouchStart}
                        >
                            {isCompleted ? (
                                <i className="bi bi-check-lg"></i>
                            ) : (
                                <i className="bi bi-chevron-double-right"></i>
                            )}
                        </div>
                    </div>
                </div>

                <div className="slide-confirm-footer">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onCancel}
                    >
                        Abbrechen
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SlideToConfirm;
